import catalog from './sharedFieldLinks.json';
import { instanceKey } from '../compass/compassModel';

const ACDF = 'Anterior cervical discectomy and fusion (ACDF)';
const APPROACHES = {
  acdf: { alliance: 'ACDF' },
  alif: { alliance: 'ALIF' },
  dlif: { alliance: 'DLIF/LLIF' },
  olif: { alliance: 'OLIF' },
  plif: { alliance: 'PLIF' },
  tlif: { alliance: 'TLIF' },
  midlif: { alliance: 'MIDLIF' },
};
const LUMBAR_ROLES = ['alif', 'dlif', 'olif', 'plif', 'tlif'];

export function emptyCompassState() {
  return { arm: 'Cervical', region: '', active: null, forms: {}, visits: {} };
}

export function emptyAllianceState() {
  return { active: null, forms: {} };
}

function trimNumber(value, places) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  const factor = 10 ** places;
  const rounded = Math.round(number * factor) / factor;
  return String(rounded);
}

function formKey(binding) {
  if (!binding || binding.virtual || !binding.formId) return '';
  return instanceKey(binding.formId, binding.visitId);
}

function readField(state, binding) {
  if (!binding || binding.virtual || !binding.formId) return undefined;
  return state?.forms?.[formKey(binding)]?.values?.[binding.field];
}

function writeField(state, binding, value) {
  if (!binding || binding.virtual || !binding.formId) return state;
  const key = formKey(binding);
  const form = state.forms?.[key] || { values: {}, repeats: {} };
  if (Object.is(form.values?.[binding.field], value)) return state;
  return {
    ...state,
    forms: {
      ...(state.forms || {}),
      [key]: { ...form, values: { ...(form.values || {}), [binding.field]: value } },
    },
  };
}

function studyBindings(slot, study, role) {
  return slot.bindings.filter((item) => item.study === study && (!role || item.role === role));
}

function firstBinding(slot, study, role) {
  return studyBindings(slot, study, role)[0] || null;
}

function bindingMatches(binding, fieldKey, formInstanceKey) {
  if (binding.field !== fieldKey) return false;
  if (binding.virtual || !formInstanceKey) return true;
  return formKey(binding) === formInstanceKey;
}

function locationsFor(fieldKey, formInstanceKey) {
  const found = [];
  catalog.concepts.forEach((concept) => {
    concept.slots.forEach((slot) => {
      if (slot.bindings.some((item) => bindingMatches(item, fieldKey, formInstanceKey))) {
        found.push({ concept, slot });
      }
    });
  });
  return found;
}

function isPresent(canonical) {
  return Boolean(canonical && !canonical.skip && !canonical.empty);
}

function readCopy(slot, study, state) {
  const values = studyBindings(slot, study).map((item) => readField(state, item));
  const found = values.find((value) => value != null && value !== '');
  if (found == null) return { empty: true };
  return { text: String(found) };
}

function writeCopy(slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const text = canonical?.empty ? '' : canonical.text ?? '';
  return studyBindings(slot, study).reduce((next, item) => writeField(next, item, text), state);
}

function readNumber(slot, study, state) {
  const raw = readField(state, studyBindings(slot, study)[0]);
  if (raw == null || raw === '') return { empty: true };
  const number = Number(raw);
  if (!Number.isFinite(number)) return { skip: true };
  return { number };
}

function writeNumber(concept, slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const text = canonical?.empty ? '' : trimNumber(canonical.number, concept.places ?? 1);
  return studyBindings(slot, study).reduce((next, item) => writeField(next, item, text), state);
}

function choiceBinding(slot, study) {
  return studyBindings(slot, study).find((item) => !item.virtual && item.role === 'value')
    || studyBindings(slot, study).find((item) => !item.virtual);
}

function readChoice(concept, slot, study, state) {
  const binding = choiceBinding(slot, study);
  const value = readField(state, binding);
  if (value == null || value === '') return { empty: true };
  const found = (concept.choices || []).find((choice) => (choice.read?.[study] || []).includes(value));
  if (!found) return { skip: true };
  return { id: found.canonical };
}

function writeChoice(concept, slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const binding = choiceBinding(slot, study);
  if (!binding) return state;
  if (canonical?.empty) return writeField(state, binding, '');
  const choice = (concept.choices || []).find((item) => item.canonical === canonical.id);
  if (!choice) return state;
  const current = readField(state, binding);
  if (concept.preserveSpecific && (choice.read?.[study] || []).includes(current)) return state;
  if (!Object.prototype.hasOwnProperty.call(choice.write || {}, study)) return state;
  return writeField(state, binding, choice.write[study]);
}

function readAge(slot, study, state) {
  const yearsBinding = firstBinding(slot, study, 'years');
  const ninetyBinding = firstBinding(slot, study, 'ninety');
  const raw = yearsBinding ? readField(state, yearsBinding) : undefined;
  const flag = ninetyBinding ? readField(state, ninetyBinding) : undefined;
  const years = raw == null || raw === '' ? null : Number(raw);
  const finite = Number.isFinite(years) ? years : null;
  if (raw != null && raw !== '' && finite == null) return { skip: true };
  let ninety = flag === 'Yes' ? true : flag === 'No' ? false : null;
  if (finite != null) ninety = finite >= 90;
  if (finite == null && ninety == null) return { empty: true };
  return { years: finite, ninety };
}

function writeAge(slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const yearsBinding = firstBinding(slot, study, 'years');
  const ninetyBinding = firstBinding(slot, study, 'ninety');
  let next = state;
  if (canonical?.empty) {
    if (yearsBinding) next = writeField(next, yearsBinding, '');
    if (ninetyBinding) next = writeField(next, ninetyBinding, '');
    return next;
  }
  if (yearsBinding && canonical.years != null) {
    next = writeField(next, yearsBinding, trimNumber(canonical.years, 0));
  }
  if (ninetyBinding && canonical.ninety != null) {
    next = writeField(next, ninetyBinding, canonical.ninety ? 'Yes' : 'No');
  }
  return next;
}

function readMeasure(concept, slot, study, state) {
  const valueBinding = firstBinding(slot, study, 'value');
  const unitBinding = firstBinding(slot, study, 'unit');
  const raw = readField(state, valueBinding);
  if (raw == null || raw === '') return { empty: true };
  const number = Number(raw);
  if (!Number.isFinite(number)) return { skip: true };
  const unit = (unitBinding && readField(state, unitBinding)) || concept.canonicalUnit;
  const factor = concept.perCanonical?.[unit];
  if (!factor) return { skip: true };
  return { value: number / factor };
}

function writeMeasure(concept, slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const valueBinding = firstBinding(slot, study, 'value');
  const unitBinding = firstBinding(slot, study, 'unit');
  if (canonical?.empty) return writeField(state, valueBinding, '');
  let next = state;
  let unit = concept.canonicalUnit;
  if (unitBinding) {
    const current = readField(state, unitBinding);
    if (current && concept.perCanonical?.[current]) unit = current;
    else next = writeField(next, unitBinding, concept.canonicalUnit);
  }
  const stored = canonical.value * concept.perCanonical[unit];
  return writeField(next, valueBinding, trimNumber(stored, concept.places ?? 1));
}

function readRace(concept, slot, study, state) {
  const multi = firstBinding(slot, study, 'multi');
  const single = firstBinding(slot, study, 'single');
  if (multi) {
    const values = readField(state, multi);
    if (!Array.isArray(values) || !values.length) return { empty: true };
    return { labels: values };
  }
  const value = readField(state, single);
  if (!value) return { empty: true };
  const pair = (concept.pairs || []).find((item) => item[1] === value);
  if (!pair) return { skip: true };
  return { labels: [pair[0]] };
}

function writeRace(concept, slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const multi = firstBinding(slot, study, 'multi');
  const single = firstBinding(slot, study, 'single');
  if (canonical?.empty) {
    if (multi) return writeField(state, multi, []);
    if (single) return writeField(state, single, '');
    return state;
  }
  if (multi) return writeField(state, multi, canonical.labels || []);
  if (!single || (canonical.labels || []).length !== 1) return state;
  const pair = (concept.pairs || []).find((item) => item[0] === canonical.labels[0]);
  if (!pair) return state;
  return writeField(state, single, pair[1]);
}

function readYes(slot, study, state) {
  const yesNo = firstBinding(slot, study, 'yes_no');
  if (yesNo) {
    const value = readField(state, yesNo);
    if (value === 'Yes') return { value: true };
    if (value === 'No') return { value: false };
    if (value == null || value === '') return { empty: true };
    return { skip: true };
  }
  const flags = studyBindings(slot, study, 'flag');
  if (flags.some((item) => readField(state, item) === true)) return { value: true };
  if (flags.some((item) => readField(state, item) === false)) return { value: false };
  return { empty: true };
}

function writeYes(slot, study, state, canonical) {
  if (canonical?.skip) return state;
  let next = state;
  const yesNo = firstBinding(slot, study, 'yes_no');
  if (yesNo) next = writeField(next, yesNo, canonical?.empty ? '' : canonical.value ? 'Yes' : 'No');
  studyBindings(slot, study, 'flag').forEach((item) => {
    next = writeField(next, item, !canonical?.empty && Boolean(canonical.value));
  });
  if (canonical?.value) {
    studyBindings(slot, study, 'gate').forEach((item) => {
      next = writeField(next, item, 'Yes');
    });
  }
  return next;
}

function readRegion(study, state, slot) {
  if (study === 'alliance') {
    const value = readField(state, slot.bindings.find((item) => item.study === 'alliance'));
    if (!value) return { empty: true };
    if (value === 'Cervical') return { id: 'cervical' };
    if (value === 'Thoracic, lumbar, and/or sacral') return { id: 'thoracolumbar' };
    if (value === 'Both') return { id: 'both' };
    return { skip: true };
  }
  if (state?.arm === 'Cervical') return { id: 'cervical' };
  if (state?.arm === 'Thoracolumbar') return { id: 'thoracolumbar' };
  if (state?.arm === 'Adult Spinal Deformity' && state?.region === 'Cervical') return { id: 'cervical' };
  if (state?.arm === 'Adult Spinal Deformity' && state?.region === 'Thoracolumbar') return { id: 'thoracolumbar' };
  return { skip: true };
}

function writeRegion(slot, study, state, canonical) {
  if (canonical?.skip || canonical?.empty || !canonical?.id) return state;
  if (study === 'alliance') {
    const label = canonical.id === 'cervical'
      ? 'Cervical'
      : canonical.id === 'thoracolumbar'
        ? 'Thoracic, lumbar, and/or sacral'
        : canonical.id === 'both'
          ? 'Both'
          : '';
    if (!label) return state;
    return writeField(state, slot.bindings.find((item) => item.study === 'alliance'), label);
  }
  if (canonical.id === 'both') return state;
  const region = canonical.id === 'cervical' ? 'Cervical' : 'Thoracolumbar';
  const asd = firstBinding(slot, 'compass', 'asd_region');
  if (state?.arm === 'Adult Spinal Deformity') {
    return writeField({ ...state, region }, asd, region);
  }
  return { ...state, arm: region === 'Cervical' ? 'Cervical' : 'Thoracolumbar' };
}

function readApproach(slot, study, state) {
  if (study === 'alliance') {
    const value = readField(state, slot.bindings.find((item) => item.study === 'alliance'));
    if (!value) return { empty: true };
    const found = Object.entries(APPROACHES).find(([, item]) => item.alliance === value);
    return found ? { id: found[0] } : { skip: true };
  }
  const cervical = readField(state, firstBinding(slot, 'compass', 'cervical'));
  const lumbar = LUMBAR_ROLES.filter((role) => readField(state, firstBinding(slot, 'compass', role)) === true);
  const acdf = cervical === ACDF;
  if (acdf && lumbar.length === 0) return { id: 'acdf' };
  if (!acdf && lumbar.length === 1) return { id: lumbar[0] };
  if (!cervical && lumbar.length === 0) return { empty: true };
  return { skip: true };
}

function writeApproach(slot, study, state, canonical) {
  if (canonical?.skip) return state;
  if (study === 'alliance') {
    const binding = slot.bindings.find((item) => item.study === 'alliance');
    if (canonical?.empty) return writeField(state, binding, '');
    const item = APPROACHES[canonical.id];
    return item ? writeField(state, binding, item.alliance) : state;
  }
  if (canonical?.id === 'midlif') return state;
  let next = state;
  const cervical = firstBinding(slot, 'compass', 'cervical');
  next = writeField(next, cervical, !canonical?.empty && canonical.id === 'acdf' ? ACDF : '');
  LUMBAR_ROLES.forEach((role) => {
    next = writeField(next, firstBinding(slot, 'compass', role), !canonical?.empty && canonical.id === role);
  });
  return next;
}

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function readGraft(concept, slot, study, state) {
  const materials = concept.materials || [];
  if (study === 'alliance') {
    const types = asList(readField(state, firstBinding(slot, 'alliance', 'types')));
    const ids = materials.filter((item) => types.includes(item[2])).map((item) => item[0]);
    return ids.length ? { ids } : { empty: true };
  }
  const used = new Set([
    ...asList(readField(state, firstBinding(slot, 'compass', 'inside'))),
    ...asList(readField(state, firstBinding(slot, 'compass', 'outside'))),
  ]);
  const ids = materials.filter((item) => used.has(item[1])).map((item) => item[0]);
  return ids.length ? { ids } : { empty: true };
}

function writeGraft(concept, slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const materials = concept.materials || [];
  if (study === 'alliance') {
    const typesBinding = firstBinding(slot, 'alliance', 'types');
    if (canonical?.empty) return writeField(state, typesBinding, []);
    const labels = materials.filter((item) => canonical.ids.includes(item[0])).map((item) => item[2]);
    let next = writeField(state, typesBinding, labels);
    if (labels.length) next = writeField(next, firstBinding(slot, 'alliance', 'used'), 'Yes');
    return next;
  }
  const inside = firstBinding(slot, 'compass', 'inside');
  const outside = firstBinding(slot, 'compass', 'outside');
  if (canonical?.empty) return writeField(state, inside, ['None']);
  const wanted = materials.filter((item) => canonical.ids.includes(item[0])).map((item) => item[1]);
  const outsideNow = asList(readField(state, outside)).filter((label) => label && label !== 'None');
  const outsideKept = outsideNow.filter((label) => wanted.includes(label));
  const insideLabels = wanted.filter((label) => !outsideKept.includes(label));
  let next = writeField(state, inside, insideLabels.length ? insideLabels : ['None']);
  if (outsideNow.length) {
    next = writeField(next, outside, outsideKept.length ? outsideKept : ['None']);
  }
  return next;
}

function readScore(concept, slot, study, state) {
  const raw = readField(state, firstBinding(slot, study, 'score'));
  if (raw == null || raw === '') return { empty: true };
  const number = Number(raw);
  if (!Number.isFinite(number)) return { skip: true };
  if (concept.kind === 'vas' && study === 'alliance') {
    const scale = readField(state, firstBinding(slot, 'alliance', 'scale'));
    if (scale === 'Other') return { skip: true };
    if (scale === '0 to 100') return { score: number / 10 };
    return { score: number };
  }
  return { score: number };
}

function writeScore(concept, slot, study, state, canonical) {
  if (canonical?.skip) return state;
  const score = firstBinding(slot, study, 'score');
  if (canonical?.empty) return writeField(state, score, '');
  let next = state;
  let stored = canonical.score;
  if (concept.kind === 'vas' && study === 'alliance') {
    const scaleBinding = firstBinding(slot, 'alliance', 'scale');
    const scale = readField(state, scaleBinding);
    if (scale === 'Other') return state;
    if (scale === '0 to 100') stored *= 10;
    else next = writeField(next, scaleBinding, '0 to 10');
  }
  next = writeField(next, score, trimNumber(stored, 1));
  const completed = firstBinding(slot, study, 'completed');
  if (completed) next = writeField(next, completed, 'Yes');
  return next;
}

function readCanonical(concept, slot, study, state) {
  switch (concept.kind) {
    case 'copy': return readCopy(slot, study, state);
    case 'number': return readNumber(slot, study, state);
    case 'choice': return readChoice(concept, slot, study, state);
    case 'age': return readAge(slot, study, state);
    case 'measure': return readMeasure(concept, slot, study, state);
    case 'race': return readRace(concept, slot, study, state);
    case 'yes_boolean': return readYes(slot, study, state);
    case 'region': return readRegion(study, state, slot);
    case 'approach': return readApproach(slot, study, state);
    case 'graft': return readGraft(concept, slot, study, state);
    case 'score':
    case 'vas':
      return readScore(concept, slot, study, state);
    default:
      return { skip: true };
  }
}

function writeCanonical(concept, slot, study, state, canonical) {
  switch (concept.kind) {
    case 'copy': return writeCopy(slot, study, state, canonical);
    case 'number': return writeNumber(concept, slot, study, state, canonical);
    case 'choice': return writeChoice(concept, slot, study, state, canonical);
    case 'age': return writeAge(slot, study, state, canonical);
    case 'measure': return writeMeasure(concept, slot, study, state, canonical);
    case 'race': return writeRace(concept, slot, study, state, canonical);
    case 'yes_boolean': return writeYes(slot, study, state, canonical);
    case 'region': return writeRegion(slot, study, state, canonical);
    case 'approach': return writeApproach(slot, study, state, canonical);
    case 'graft': return writeGraft(concept, slot, study, state, canonical);
    case 'score':
    case 'vas':
      return writeScore(concept, slot, study, state, canonical);
    default:
      return state;
  }
}

function otherStudy(study) {
  return study === 'compass' ? 'alliance' : 'compass';
}

function emptyFor(study) {
  return study === 'compass' ? emptyCompassState() : emptyAllianceState();
}

export function applyProjection(study, state, fieldKey, formInstanceKey, otherState) {
  const locations = locationsFor(fieldKey, formInstanceKey);
  if (!locations.length) return { self: state, other: null, otherStudy: null };
  let self = state;
  let other = otherState || emptyFor(otherStudy(study));
  locations.forEach(({ concept, slot }) => {
    const canonical = readCanonical(concept, slot, study, self);
    self = writeCanonical(concept, slot, study, self, canonical);
    other = writeCanonical(concept, slot, otherStudy(study), other, canonical);
  });
  return { self, other, otherStudy: otherStudy(study) };
}

export function overlayFromOther(targetStudy, targetState, sourceStudy, sourceState) {
  if (!sourceState) return targetState;
  let next = targetState;
  catalog.concepts.forEach((concept) => {
    if (concept.kind === 'region') return;
    concept.slots.forEach((slot) => {
      const canonical = readCanonical(concept, slot, sourceStudy, sourceState);
      if (!isPresent(canonical)) return;
      next = writeCanonical(concept, slot, targetStudy, next, canonical);
    });
  });
  return next;
}

export function catalogConcepts() {
  return catalog.concepts;
}
